import { useCallback } from "react";
import { useDispatch, useSelector } from "../../redux/hooks";
import { setFeature } from "../../redux/slices/generatorSlice";

/**
 * Custom hook to handle show/hide content functionality based on checkbox state.
 * When a checkbox with feature="showContent" is checked, it shows all elements with the matching onClass.
 * When unchecked, it hides elements with the matching onClass.
 */
export const useShowContent = () => {
  const dispatch = useDispatch();
  const { features } = useSelector(state => state.generatorSlice);
  /**
   * Handles checkbox changes for showContent feature
   * @param checked - Whether the checkbox is checked
   * @param className - The onClass name to show/hide
   * @param index - Optional index for reports with multiple entries
   */
  const handleShowContent = useCallback(
    (checked: boolean, className: string, index?: number) => {
      const key =
        index !== undefined
          ? `showContent_${className}_${index}`
          : `showContent_${className}`;

      dispatch(
        setFeature({
          key,
          feature: checked,
        }),
      );
    },
    [dispatch],
  );
  /**
   * Checks if content with the given onClass should be visible
   * @param className - The onClass name to check
   * @param index - Optional index for reports with multiple entries
   * @returns boolean indicating if content should be visible
   */
  const isContentVisible = useCallback(
    (className?: string, index?: number) => {
      if (!className) return true;
      const key =
        index !== undefined
          ? `showContent_${className}_${index}`
          : `showContent_${className}`;
      return features[key] === true;
    },
    [features],
  ); /**
   * Determines if an element should be hidden based on its feature and onClass properties
   * @param element - The element with potential feature and onClass properties
   * @param index - Optional index for reports with multiple entries
   * @returns boolean indicating if element should be hidden
   */
  const shouldHideElement = useCallback(
    (
      element: {
        feature?: string;
        onClass?: string;
        title?: string;
        hidden?: boolean;
      },
      index?: number,
    ) => {
      // For elements with feature="showContent" and an onClass, check if their showContent feature is enabled
      if (element.feature === "showContent" && element.onClass) {
        return !isContentVisible(element.onClass, index);
      }

      // For elements with feature="hidden" and an onClass, check if their showContent feature is enabled
      if (element.feature === "hidden" && element.onClass) {
        const isVisible = isContentVisible(element.onClass, index);
        return !isVisible;
      }

      // For sections with an onClass and hidden: true property
      // Hide them by default unless the corresponding showContent feature is enabled
      // TODO remove after Chris changes section level hidden to feature="hidden"
      if (element.onClass && element.hidden === true) {
        const isVisible = isContentVisible(element.onClass, index);
        return !isVisible;
      }

      return false;
    },
    [isContentVisible],
  );
  return {
    handleShowContent,
    isContentVisible,
    shouldHideElement,
  };
};
